import { mapGhlSurveyFromDoc, type GhlSurveySnapshot } from '@/lib/ghl-survey-answers';
import type { GhlBookingRecord } from '@/lib/ghl-booking';
import { normalizeEmail } from '@/lib/shared-user-roles';

export type { GhlSurveyAnswer, GhlSurveySnapshot } from '@/lib/ghl-survey-answers';

export const CLIENT_PROSPECTS_COLLECTION = 'client-prospects';
export const POSTURE_ANALYSES_SUBCOLLECTION = 'postureAnalyses';
export const PROSPECT_ID_RE = /^[a-zA-Z0-9_-]{10,128}$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export type ProspectListItem = {
  id: string;
  name: string;
  email: string;
  gdprConsent: boolean;
  pictureConsent: boolean;
  deviceType?: 'mobile' | 'desktop';
  userAgent?: string;
  createdAt: string | null;
  calendlyBooking?: Record<string, unknown>;
  calendlyBookedAt?: string | null;
  ghlCalendarOpenedAt?: string | null;
  ghlCalendarBookedAt?: string | null;
  /** Timestamp when the GHL survey widget was first opened (Q2 funnel stage). */
  surveyOpenedAt?: string | null;
  ghlSurvey?: GhlSurveySnapshot;
  ghlBooking?: GhlBookingRecord;
  ghlBookedAt?: string | null;
  source?: string;
};

export type GhlCalendarEvent = 'opened' | 'booked';
export type GhlCalendarStatus = 'none' | 'opened' | 'booked';

export function getGhlCalendarStatus(prospect: {
  ghlCalendarOpenedAt?: string | null;
  ghlCalendarBookedAt?: string | null;
}): GhlCalendarStatus {
  if (prospect.ghlCalendarBookedAt) return 'booked';
  if (prospect.ghlCalendarOpenedAt) return 'opened';
  return 'none';
}

export type ProspectDetail = ProspectListItem & {
  postureAnalyses: Array<Record<string, unknown> & { id: string }>;
};

export function normalizeProspectEmail(email: string): string {
  return normalizeEmail(email);
}

export function isCompleteEmailQuery(query: string): boolean {
  return EMAIL_RE.test(normalizeProspectEmail(query));
}

export function serializeTimestamp(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
  }
  if (typeof value === 'object') {
    const ts = value as { toDate?: () => Date; toMillis?: () => number };
    if (typeof ts.toDate === 'function') {
      return ts.toDate().toISOString();
    }
    if (typeof ts.toMillis === 'function') {
      return new Date(ts.toMillis()).toISOString();
    }
  }
  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function prospectMatchesSearch(
  prospect: { email?: string; name?: string },
  query: string,
): boolean {
  const q = normalizeProspectEmail(query);
  if (!q) return true;
  const email = normalizeProspectEmail(prospect.email || '');
  const name = (prospect.name || '').toLowerCase();
  if (email === q || email.includes(q)) return true;
  return name.includes(q);
}

export function salesProspectPath(prospectId: string): string {
  return `/sales/prospects/${prospectId}`;
}

export function toClientPostureAnalysisCopy(
  analysisId: string,
  data: Record<string, unknown>,
  params: { clientId: string; createdBy: string; sourceProspectId: string },
): Record<string, unknown> {
  const { id: _ignoredId, ...rest } = data;
  return {
    ...rest,
    clientId: params.clientId,
    sourceProspectId: params.sourceProspectId,
    sourceAnalysisId: analysisId,
    _createdBy: params.createdBy,
  };
}

export function mapProspectDoc(id: string, data: Record<string, unknown>): ProspectListItem {
  return {
    id,
    name: typeof data.name === 'string' ? data.name : '',
    email: typeof data.email === 'string' ? data.email : '',
    gdprConsent: data.gdprConsent === true,
    pictureConsent: data.pictureConsent === true,
    deviceType: data.deviceType === 'mobile' || data.deviceType === 'desktop' ? data.deviceType : undefined,
    userAgent: typeof data.userAgent === 'string' ? data.userAgent : undefined,
    createdAt: serializeTimestamp(data.createdAt),
    calendlyBooking:
      data.calendlyBooking && typeof data.calendlyBooking === 'object'
        ? (data.calendlyBooking as Record<string, unknown>)
        : undefined,
    calendlyBookedAt: serializeTimestamp(data.calendlyBookedAt),
    ghlCalendarOpenedAt: serializeTimestamp(data.ghlCalendarOpenedAt),
    ghlCalendarBookedAt: serializeTimestamp(data.ghlCalendarBookedAt),
    surveyOpenedAt: serializeTimestamp(data.surveyOpenedAt),
    ghlSurvey: mapGhlSurveyFromDoc(data.ghlSurvey),
    ghlBooking:
      data.ghlBooking && typeof data.ghlBooking === 'object' && !Array.isArray(data.ghlBooking)
        ? (data.ghlBooking as GhlBookingRecord)
        : undefined,
    ghlBookedAt: serializeTimestamp(data.ghlBookedAt),
    source: typeof data.source === 'string' ? data.source : undefined,
  };
}
